import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface NotificationPreferences {
  id: string;
  user_id: string;
  tenant_id: string;
  hot_lead_alerts: boolean;
  deal_stage_changes: boolean;
  scheduled_call_reminders: boolean;
  new_lead_alerts: boolean;
  hot_lead_threshold: number;
  call_reminder_minutes: number;
  email_enabled: boolean;
  daily_digest_enabled: boolean;
  push_notifications_enabled: boolean;
  created_at: string;
  updated_at: string;
}

const defaultPreferences = {
  hot_lead_alerts: true,
  deal_stage_changes: true,
  scheduled_call_reminders: true,
  new_lead_alerts: false,
  hot_lead_threshold: 80,
  call_reminder_minutes: 30,
  email_enabled: true,
  daily_digest_enabled: false,
  push_notifications_enabled: true,
};

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ["notification_preferences"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      
      // Return data or defaults
      if (data) return data as NotificationPreferences;
      return { ...defaultPreferences, user_id: user.id } as Partial<NotificationPreferences>;
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .single();

      if (!profile?.tenant_id) throw new Error("No tenant assigned");

      // Check if preferences exist
      const { data: existing } = await supabase
        .from("notification_preferences")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        // Update existing
        const { data, error } = await supabase
          .from("notification_preferences")
          .update(updates)
          .eq("user_id", user.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new
        const { data, error } = await supabase
          .from("notification_preferences")
          .insert({
            user_id: user.id,
            tenant_id: profile.tenant_id,
            ...defaultPreferences,
            ...updates,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification_preferences"] });
      toast.success("Notification preferences saved");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}
